<script setup lang="ts">
/**
 * The way in. On a phone a browser tab loses the tide tables, so the tab is a
 * dead end: Safari on iOS gets the add-to-home-screen instructions, any other
 * iOS browser gets handed to Safari, and Android may skip. On a desktop the
 * browser keeps its storage, so there it is only a hint.
 */
import { onMounted } from 'vue'
import { useAppStore } from '../store'
import { computed } from 'vue'
import { useInstallGate, safariUrl, desktopBrowser, isIpad, iosVersion } from '../lib/install'
import type { MessageKey } from '../i18n'

const store = useAppStore()
const { mode, promptable, skip, install } = useInstallGate()

/** Name the menu this browser actually has; the rest get the two-browser summary */
const desktopHow = computed<MessageKey>(() => {
  const how = { chrome: 'desktopHowChrome', edge: 'desktopHowEdge', firefox: 'desktopHowFirefox', safari: 'desktopHowSafari', other: 'desktopHow' } as const
  return how[desktopBrowser()]
})

/**
 * Where Safari keeps the share button, which moved.
 *
 * Up to iOS 18 it is on the toolbar itself: at the bottom of an iPhone, top right on an iPad.
 * Safari 26 took it off the bar: on an iPhone it is in the menu behind the button with three
 * lines at the left of the address field, on an iPad behind a ••• menu, and the share sheet now hides "Add to Home Screen" under "More" until it is used once. Since the
 * reader is holding the device and we are guessing from a user-agent string, the step names
 * the likely one and a line underneath covers the other.
 */
const newSafari = computed(() => (iosVersion() ?? 0) >= 26)
const shareStep = computed<MessageKey>(() =>
  newSafari.value ? (isIpad() ? 'iosShareDots' : 'iosShareMenu') : isIpad() ? 'iosShareIconIpad' : 'iosShareIcon')
/** Only Safari 26 hides "Add to Home Screen" under More, so only there is that worth saying */
const pickStep = computed<MessageKey>(() => (newSafari.value ? 'iosStep2New' : 'iosStep2'))
/** Only Safari 26 asks whether to open it as a web app, and only there is that the thing to say */
const addStep = computed<MessageKey>(() => (newSafari.value ? 'iosStep3Web' : 'iosStep3'))
/** The line for a Safari that looks different from what we guessed, about its own version only */
const elsewhere = computed<MessageKey>(() => (newSafari.value ? 'iosShareElsewhereNew' : 'iosShareElsewhere'))

const TRIED = 'quicktide.safari'
onMounted(() => {
  // Chrome, Firefox and Edge for iOS understand the scheme and move the page across
  // (Edge asks first); browsers that ignore it simply leave the instructions on
  // screen. Once per visit, so a user who comes back on purpose is not bounced again.
  if (mode.value === 'iosOther' && !sessionStorage.getItem(TRIED)) {
    sessionStorage.setItem(TRIED, 'tried')
    location.href = safariUrl()
  }
})
</script>

<template>
  <!-- On another iOS browser: this one can never install, so send them to Safari -->
  <div v-if="mode === 'iosOther'" class="gate">
    <h1>{{ store.t('installTitle') }}</h1>
    <p>{{ store.t('installWhy') }}</p>
    <p class="strong">{{ store.t('safariOnly') }}</p>
    <a class="button primary big" :href="safariUrl()">{{ store.t('openInSafari') }}</a>
    <div class="safaribox">
      <svg viewBox="0 0 64 64" class="glyph safari" aria-hidden="true">
        <defs>
          <linearGradient id="safariring" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#3ac0f7" /><stop offset="1" stop-color="#1573e8" />
          </linearGradient>
        </defs>
        <circle cx="32" cy="32" r="30" fill="url(#safariring)" />
        <circle cx="32" cy="32" r="24" fill="#fdfdfd" />
        <polygon points="45,19 36.2,36.2 27.8,27.8" fill="#f4453c" />
        <polygon points="19,45 36.2,36.2 27.8,27.8" fill="#d3d6da" />
      </svg>
      <span>{{ store.t('safariLabel') }}</span>
    </div>
    <p class="small muted">{{ store.t('safariHow') }}</p>
  </div>

  <!-- In Safari, in a tab: show the two taps that make it an app -->
  <div v-else-if="mode === 'iosSafari'" class="gate">
    <h1>{{ store.t('installTitle') }}</h1>
    <p>{{ store.t('installWhy') }}</p>
    <ol class="steps">
      <li>
        <!-- Safari 26: the menu button at the left of the address field on an iPhone, ••• on an
             iPad; before it the share arrow -->
        <svg v-if="newSafari && !isIpad()" viewBox="0 0 24 24" class="glyph" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
        <svg v-else-if="newSafari" viewBox="0 0 24 24" class="glyph" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
        <svg v-else viewBox="0 0 24 24" class="glyph" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
        </svg>
        <span>{{ store.t(shareStep) }}</span>
      </li>
      <li>
        <svg viewBox="0 0 24 24" class="glyph" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="4" /><path d="M12 8v8M8 12h8" />
        </svg>
        <span>{{ store.t(pickStep) }}</span>
      </li>
      <li>
        <svg viewBox="0 0 24 24" class="glyph" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M4 12.5l5 5L20 6.5" />
        </svg>
        <span>{{ store.t(addStep) }}</span>
      </li>
    </ol>
    <p class="small muted">{{ store.t(elsewhere) }}</p>
    <p class="small muted">{{ store.t('openViaIcon') }}</p>
  </div>

  <!-- Android can usually install itself; if not, the menu does it. Skipping is allowed -->
  <div v-else-if="mode === 'android'" class="gate">
    <h1>{{ store.t('installTitle') }}</h1>
    <p>{{ store.t('installWhyGeneral') }}</p>
    <button v-if="promptable" class="primary big" @click="install()">{{ store.t('installNow') }}</button>
    <ol v-else class="steps">
      <li>
        <svg viewBox="0 0 24 24" class="glyph" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
        <span>{{ store.t('androidStep1') }}</span>
      </li>
      <li>
        <svg viewBox="0 0 24 24" class="glyph" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 4v10" /><path d="M8 10l4 4 4-4" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
        <span>{{ store.t('androidStep2') }}</span>
      </li>
    </ol>
    <button class="link big" @click="skip()">{{ store.t('skipInstall') }}</button>
  </div>

  <!-- On a desktop the tab keeps its data, so this is a notice, not a door -->
  <div v-else-if="mode === 'desktop'" class="installhint">
    <span>{{ store.t('installWhyGeneral') }} <span class="muted">{{ promptable ? '' : store.t(desktopHow) }}</span></span>
    <span class="hintbuttons">
      <button v-if="promptable" class="primary" @click="install()">{{ store.t('installNow') }}</button>
      <button class="link" @click="skip()">{{ store.t('notNow') }}</button>
    </span>
  </div>
</template>
